import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { Tooltip } from 'antd'
import styles from '../projectDetail.module.css'

const STORAGE_KEY = 'pmx.floatingViewTabs.position'
const DRAG_THRESHOLD_PX = 8
const EDGE_PADDING = 8

const readPoint = (value) =>
    typeof value?.left === 'number' && typeof value?.top === 'number'
        ? { left: value.left, top: value.top }
        : null

const readStoredState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return { position: null, freePosition: null, sticky: false }
        const parsed = JSON.parse(raw)
        const sticky = parsed?.sticky === true
        const freePosition = readPoint(parsed?.freePosition)
        // Initial expanded load stays bottom-center; restore free spot only after unpin.
        if (!sticky) {
            return { position: null, freePosition, sticky: false }
        }
        return {
            position: readPoint(parsed),
            freePosition,
            sticky: true,
        }
    } catch {
        return { position: null, freePosition: null, sticky: false }
    }
}

const clampPosition = (left, top, el, parent) => {
    if (!el || !parent) return { left, top }
    const maxLeft = Math.max(
        EDGE_PADDING,
        parent.clientWidth - el.offsetWidth - EDGE_PADDING
    )
    const maxTop = Math.max(
        EDGE_PADDING,
        parent.clientHeight - el.offsetHeight - EDGE_PADDING
    )
    return {
        left: Math.min(Math.max(left, EDGE_PADDING), maxLeft),
        top: Math.min(Math.max(top, EDGE_PADDING), maxTop),
    }
}

const getCurrentLayoutPosition = (el, parent) => {
    if (!el || !parent) return null
    const elRect = el.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()
    return clampPosition(
        elRect.left - parentRect.left,
        elRect.top - parentRect.top,
        el,
        parent
    )
}

const getRightEdgePosition = (el, parent, preferredTop) => {
    if (!el || !parent) return null
    const left = Math.max(
        EDGE_PADDING,
        parent.clientWidth - el.offsetWidth - EDGE_PADDING
    )
    const centeredTop = Math.max(
        EDGE_PADDING,
        (parent.clientHeight - el.offsetHeight) / 2
    )
    const top = typeof preferredTop === 'number' ? preferredTop : centeredTop
    return clampPosition(left, top, el, parent)
}

/**
 * Floating view switcher (Board / List / …).
 * Expanded: icons + labels. Pinned: collapsed icon rail on the right edge.
 */
const FloatingViewTabs = ({
    tabs = [],
    activeKey,
    onChange,
    'aria-label': ariaLabel = 'View',
}) => {
    const rootRef = useRef(null)
    const dragRef = useRef(null)
    const positionRef = useRef(null)
    const stickyRef = useRef(false)
    const freePositionRef = useRef(null)
    const suppressClickRef = useRef(false)
    const initial = useRef(null)
    if (initial.current === null) initial.current = readStoredState()
    const [position, setPosition] = useState(() => initial.current.position)
    const [sticky, setSticky] = useState(() => initial.current.sticky)
    const [isDragging, setIsDragging] = useState(false)

    if (freePositionRef.current === null && initial.current.freePosition) {
        freePositionRef.current = initial.current.freePosition
    }

    positionRef.current = position
    stickyRef.current = sticky

    const persistState = useCallback((nextPosition, nextSticky, freePosition) => {
        try {
            const payload = {
                sticky: Boolean(nextSticky),
            }
            if (nextSticky && nextPosition) {
                payload.left = nextPosition.left
                payload.top = nextPosition.top
            }
            if (freePosition) {
                payload.freePosition = freePosition
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
        } catch {
            // ignore
        }
    }, [])

    const reclamp = useCallback(() => {
        const el = rootRef.current
        const parent = el?.offsetParent
        if (!el || !parent) return

        if (stickyRef.current) {
            const next = getRightEdgePosition(
                el,
                parent,
                positionRef.current?.top
            )
            if (!next) return
            const prev = positionRef.current
            if (prev && next.left === prev.left && next.top === prev.top) return
            positionRef.current = next
            setPosition(next)
            persistState(next, true, freePositionRef.current)
            return
        }

        if (!positionRef.current) return

        setPosition((prev) => {
            if (!prev) return prev
            const next = clampPosition(prev.left, prev.top, el, parent)
            if (next.left === prev.left && next.top === prev.top) return prev
            positionRef.current = next
            return next
        })
    }, [persistState])

    useEffect(() => {
        window.addEventListener('resize', reclamp)
        return () => window.removeEventListener('resize', reclamp)
    }, [reclamp])

    // Remeasure after expand/collapse layout settles (pin/unpin handled separately).
    useEffect(() => {
        if (!sticky) return undefined
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(reclamp)
        })
        return () => cancelAnimationFrame(id)
    }, [reclamp, tabs.length, activeKey, sticky])

    const handleToggleSticky = (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (suppressClickRef.current) return

        const el = rootRef.current
        const parent = el?.offsetParent

        if (sticky) {
            // Unpin → return to the exact place where the user pinned from.
            const restored = freePositionRef.current
            setSticky(false)
            stickyRef.current = false
            if (restored) {
                positionRef.current = restored
                setPosition(restored)
                // Remeasure after expanded layout so width change doesn't clip it.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const node = rootRef.current
                        const host = node?.offsetParent
                        if (!node || !host || !freePositionRef.current) return
                        const next = clampPosition(
                            freePositionRef.current.left,
                            freePositionRef.current.top,
                            node,
                            host
                        )
                        positionRef.current = next
                        freePositionRef.current = next
                        setPosition(next)
                    })
                })
            } else {
                positionRef.current = null
                setPosition(null)
            }
            persistState(null, false, restored)
            return
        }

        // Remember where we pinned from (bottom-center or current free drag spot).
        const fromPosition =
            positionRef.current || getCurrentLayoutPosition(el, parent)
        freePositionRef.current = fromPosition
        setSticky(true)
        stickyRef.current = true
        persistState(positionRef.current, true, fromPosition)
    }

    const handlePointerDown = (event) => {
        if (event.button !== 0) return
        // Pin button handles its own click — never start a drag from it.
        if (event.target.closest(`.${styles.floatingViewStickyBtn}`)) return
        // Free mode: only drag from bar chrome so tab clicks stay reliable.
        if (!stickyRef.current && event.target.closest('button')) return

        const el = rootRef.current
        const parent = el?.offsetParent
        if (!el || !parent) return

        const elRect = el.getBoundingClientRect()
        const parentRect = parent.getBoundingClientRect()
        const current = positionRef.current
        const edgeLocked = stickyRef.current

        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originLeft: current?.left ?? elRect.left - parentRect.left,
            originTop: current?.top ?? elRect.top - parentRect.top,
            moved: false,
            edgeLocked,
        }
        suppressClickRef.current = false

        const onMove = (moveEvent) => {
            const drag = dragRef.current
            if (!drag || moveEvent.pointerId !== drag.pointerId) return

            const dx = moveEvent.clientX - drag.startX
            const dy = moveEvent.clientY - drag.startY

            if (!drag.moved) {
                // Pinned: only vertical movement counts toward drag threshold
                const distance = drag.edgeLocked
                    ? Math.abs(dy)
                    : Math.hypot(dx, dy)
                if (distance < DRAG_THRESHOLD_PX) return
                drag.moved = true
                suppressClickRef.current = true
                setIsDragging(true)
            }

            moveEvent.preventDefault()
            const next = drag.edgeLocked
                ? getRightEdgePosition(el, parent, drag.originTop + dy)
                : clampPosition(
                      drag.originLeft + dx,
                      drag.originTop + dy,
                      el,
                      parent
                  )
            if (!next) return
            positionRef.current = next
            setPosition(next)
        }

        const onUp = (upEvent) => {
            if (upEvent.pointerId !== dragRef.current?.pointerId) return

            if (dragRef.current?.moved && positionRef.current) {
                if (dragRef.current.edgeLocked) {
                    persistState(
                        positionRef.current,
                        true,
                        freePositionRef.current
                    )
                } else {
                    freePositionRef.current = positionRef.current
                }
            }

            dragRef.current = null
            setIsDragging(false)
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            window.removeEventListener('pointercancel', onUp)

            if (suppressClickRef.current) {
                window.setTimeout(() => {
                    suppressClickRef.current = false
                }, 0)
            }
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        window.addEventListener('pointercancel', onUp)
    }

    const handleTabClick = (key) => (event) => {
        if (suppressClickRef.current) {
            event.preventDefault()
            event.stopPropagation()
            return
        }
        onChange?.(key)
    }

    const positionedStyle =
        position || sticky
            ? {
                  left: position?.left,
                  top: position?.top,
                  right: 'auto',
                  bottom: 'auto',
                  transform: 'none',
              }
            : undefined

    return (
        <div
            ref={rootRef}
            className={`${styles.floatingViewTabs} ${
                isDragging ? styles.floatingViewTabsDragging : ''
            } ${sticky ? styles.floatingViewTabsSticky : ''}`}
            role="tablist"
            aria-label={ariaLabel}
            data-collapsed={sticky ? 'true' : 'false'}
            style={positionedStyle}
            onPointerDown={handlePointerDown}
        >
            {tabs.map((tab) => {
                const tabButton = (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeKey === tab.key}
                        aria-label={tab.label}
                        className={`${styles.floatingViewTabBtn} ${
                            activeKey === tab.key
                                ? styles.floatingViewTabBtnActive
                                : ''
                        }`}
                        onClick={handleTabClick(tab.key)}
                    >
                        <Icon
                            icon={tab.icon}
                            width={16}
                            style={{
                                color: 'currentColor',
                                flexShrink: 0,
                                pointerEvents: 'none',
                                ...tab.iconStyle,
                            }}
                        />
                        <span className={styles.floatingViewTabLabel}>
                            {tab.label}
                        </span>
                    </button>
                )

                if (!sticky) {
                    return <React.Fragment key={tab.key}>{tabButton}</React.Fragment>
                }

                return (
                    <Tooltip
                        key={tab.key}
                        title={tab.label}
                        placement="left"
                        mouseEnterDelay={0.15}
                    >
                        {tabButton}
                    </Tooltip>
                )
            })}
            <span className={styles.floatingViewTabsDivider} aria-hidden />
            <Tooltip
                title={
                    sticky
                        ? 'Expand navigation'
                        : 'Collapse and pin to right edge'
                }
                placement={sticky ? 'left' : 'top'}
                mouseEnterDelay={0.15}
            >
                <button
                    type="button"
                    className={`${styles.floatingViewStickyBtn} ${
                        sticky ? styles.floatingViewStickyBtnActive : ''
                    }`}
                    aria-pressed={sticky}
                    aria-label={
                        sticky
                            ? 'Expand navigation'
                            : 'Collapse and pin to right edge'
                    }
                    onClick={handleToggleSticky}
                    onPointerDown={(event) => event.stopPropagation()}
                >
                    <Icon
                        icon={sticky ? 'mdi:pin' : 'mdi:pin-outline'}
                        width={16}
                        style={{ pointerEvents: 'none' }}
                    />
                </button>
            </Tooltip>
        </div>
    )
}

export default FloatingViewTabs
